import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  FileText, Download, Plus, Search, Eye, Edit2, Trash2, MoreVertical,
  ChevronLeft, ChevronRight, ArrowRight
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import JobCardModal from "@/components/JobCardModal";
import NotesHistoryModal from "@/components/NotesHistoryModal";
import InvoiceModal from "@/components/InvoiceModal";

const formatINR = (amount) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount || 0);

const STATUS_COLORS = {
  "Pending": { bg: "bg-[#FFF3E0]", text: "text-[#E65100]", dot: "bg-[#FF9800]" },
  "In Progress": { bg: "bg-[#EDE7F6]", text: "text-[#4527A0]", dot: "bg-[#7C4DFF]" },
  "Completed": { bg: "bg-[#E8F5E9]", text: "text-[#2E7D32]", dot: "bg-[#4CAF50]" },
  "Delivered": { bg: "bg-[#E0F7FA]", text: "text-[#00695C]", dot: "bg-[#26A69A]" },
};

const STATUS_OPTIONS = ["All", "Pending", "In Progress", "Completed", "Delivered"];

const JobCardsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const highlightJc = searchParams.get("highlight");
  const initialStatus = searchParams.get("status") || "All";

  const [jobCards, setJobCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(
    initialStatus === "all" ? "All" : initialStatus.split("_").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ")
  );
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCard, setEditingCard] = useState(null);
  const [notesModalOpen, setNotesModalOpen] = useState(false);
  const [selectedJobCard, setSelectedJobCard] = useState(null);
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const [invoiceJobCard, setInvoiceJobCard] = useState(null);
  const [invoiceMap, setInvoiceMap] = useState({});

  const fetchJobCards = useCallback(async () => {
    try { const r = await axios.get(`${API}/jobcards`); setJobCards(r.data); }
    catch { toast.error("Failed to fetch job cards"); }
    finally { setLoading(false); }
  }, []);

  const fetchInvoices = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/invoices`);
      const map = {}; r.data.forEach(inv => { map[inv.jobcard_id] = inv; }); setInvoiceMap(map);
    } catch (err) { console.error("Failed to fetch invoices:", err); }
  }, []);

  useEffect(() => { fetchJobCards(); fetchInvoices(); }, [fetchJobCards, fetchInvoices, location.key]);

  // Sync status filter from URL
  useEffect(() => {
    const statusParam = searchParams.get("status");
    if (statusParam) {
      const mapped = statusParam === "all" ? "All" : statusParam.split("_").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
      if (STATUS_OPTIONS.includes(mapped)) { setStatusFilter(mapped); setPage(0); }
    }
  }, [searchParams]);

  // Filter + search
  const filtered = useMemo(() => {
    let result = jobCards;
    if (statusFilter !== "All") result = result.filter(j => j.status === statusFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(j =>
        (j.job_card_number || "").toLowerCase().includes(q) ||
        (j.customer_name || "").toLowerCase().includes(q) ||
        (j.customer_contact || "").includes(q) ||
        (j.vehicle_details || "").toLowerCase().includes(q)
      );
    }
    return result;
  }, [jobCards, statusFilter, search]);

  const totalPages = Math.ceil(filtered.length / rowsPerPage);
  const paginated = filtered.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
  const startRow = page * rowsPerPage + 1;
  const endRow = Math.min((page + 1) * rowsPerPage, filtered.length);

  // Reset page when filter/search changes
  useEffect(() => { setPage(0); }, [statusFilter, search, rowsPerPage]);

  // Scroll to highlighted row
  useEffect(() => {
    if (highlightJc && !loading) {
      const idx = filtered.findIndex(j => j.job_card_number === highlightJc);
      if (idx >= 0) {
        const targetPage = Math.floor(idx / rowsPerPage);
        setPage(targetPage);
      }
    }
  }, [highlightJc, loading, filtered, rowsPerPage]);

  const handleStatusChange = (status) => {
    setStatusFilter(status);
    const param = status === "All" ? "" : status.toLowerCase().replace(/\s+/g, "_");
    const hl = highlightJc ? `&highlight=${highlightJc}` : "";
    navigate(`/job-cards${param ? `?status=${param}${hl}` : hl ? `?${hl.slice(1)}` : ""}`, { replace: true });
  };

  const handleExport = async () => {
    try {
      const r = await axios.get(`${API}/jobcards/export/excel`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const link = document.createElement('a'); link.href = url;
      link.setAttribute('download', `job_cards_${Date.now()}.xlsx`);
      document.body.appendChild(link); link.click(); link.remove();
      toast.success("Excel file downloaded");
    } catch { toast.error("Failed to export"); }
  };

  const handleDelete = async (cardId) => {
    if (!window.confirm("Delete this job card?")) return;
    try { await axios.delete(`${API}/jobcards/${cardId}`); toast.success("Deleted"); fetchJobCards(); fetchInvoices(); }
    catch { toast.error("Failed to delete"); }
  };

  const handleStatusUpdate = async (cardId, newStatus) => {
    try { await axios.put(`${API}/jobcards/${cardId}`, { status: newStatus }); toast.success("Status updated"); fetchJobCards(); }
    catch { toast.error("Failed to update status"); }
  };

  const formatDate = (d) => {
    if (!d) return "-";
    return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  };

  return (
    <div className="p-6" data-testid="jobcards-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-[22px] font-bold text-[#1E293B]">Job Cards</h1>
          <p className="text-[13px] text-[#94A3B8]">{filtered.length} job card{filtered.length !== 1 ? "s" : ""}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="export-excel-button" onClick={handleExport} variant="outline"
            className="h-9 text-[12px] gap-1.5 border-[#E2E8F0] text-[#475569]" disabled={jobCards.length === 0}>
            <Download className="w-3.5 h-3.5" /> Export to Excel
          </Button>
          <Button data-testid="create-jobcard-button" onClick={() => { setEditingCard(null); setModalOpen(true); }}
            className="bg-[#3B82F6] hover:bg-[#2563EB] text-white h-9 text-[12px] gap-1.5 font-semibold rounded-lg">
            <Plus className="w-3.5 h-3.5" /> New Job Card
          </Button>
        </div>
      </div>

      {/* Toolbar: Search + Status filter */}
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
          <Input data-testid="jobcard-search" placeholder="Search by job card #, customer, contact, or vehicle..."
            value={search} onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-10 text-[13px] bg-[#F8FAFC] border-[#E2E8F0]" />
        </div>
        <Select value={statusFilter} onValueChange={handleStatusChange}>
          <SelectTrigger data-testid="status-filter" className="w-[160px] h-10 text-[13px] bg-[#F8FAFC] border-[#E2E8F0]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        {(search || statusFilter !== "All") && (
          <button onClick={() => { setSearch(""); handleStatusChange("All"); }}
            className="text-[12px] text-[#3B82F6] font-medium hover:underline">
            Clear Filters
          </button>
        )}
      </div>

      {/* Job Cards Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-[#F1F5F9] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="bg-[#F8FAFC] text-[#94A3B8] text-[11px] font-semibold uppercase tracking-wider">
                <th className="text-left px-5 py-3">Job Card #</th>
                <th className="text-left px-3 py-3">Customer</th>
                <th className="text-left px-3 py-3">Contact</th>
                <th className="text-left px-3 py-3">Vehicle</th>
                <th className="text-left px-3 py-3">Service</th>
                <th className="text-left px-3 py-3">Status</th>
                <th className="text-left px-3 py-3">Submitted</th>
                <th className="text-left px-3 py-3">Invoice</th>
                <th className="text-right px-5 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="text-center py-16 text-[#94A3B8]">Loading...</td></tr>
              ) : paginated.length === 0 ? (
                <tr><td colSpan={9} className="text-center py-16">
                  <FileText className="w-12 h-12 mx-auto mb-3 text-[#E2E8F0]" />
                  <p className="text-[#94A3B8]">{search ? "No job cards match your search" : statusFilter !== "All" ? `No ${statusFilter.toLowerCase()} job cards` : "No job cards yet"}</p>
                </td></tr>
              ) : paginated.map((card) => {
                const invoice = invoiceMap[card.id];
                const sc = STATUS_COLORS[card.status] || STATUS_COLORS["Pending"];
                const isHighlighted = highlightJc === card.job_card_number;
                return (
                  <tr key={card.id} data-testid={`jobcard-row-${card.job_card_number}`}
                    className={`border-b transition-colors ${
                      isHighlighted
                        ? "bg-[#EFF6FF] border-[#BFDBFE] border-l-4 border-l-[#3B82F6]"
                        : "border-[#F8FAFC] hover:bg-[#FAFBFD]"
                    }`}>
                    <td className="px-5 py-3">
                      <button data-testid={`jobcard-click-${card.job_card_number}`}
                        onClick={() => { setSelectedJobCard(card); setNotesModalOpen(true); }}
                        className="text-[#3B82F6] font-semibold hover:underline">{card.job_card_number}</button>
                    </td>
                    <td className="px-3 py-3 font-medium text-[#1E293B]">{card.customer_name}</td>
                    <td className="px-3 py-3 text-[#64748B]">{card.customer_contact}</td>
                    <td className="px-3 py-3 text-[#64748B] max-w-[120px] truncate">{card.vehicle_details}</td>
                    <td className="px-3 py-3 text-[#64748B]">{card.type_of_service}</td>
                    <td className="px-3 py-3">
                      <Select value={card.status} onValueChange={(v) => handleStatusUpdate(card.id, v)}>
                        <SelectTrigger data-testid={`status-select-${card.job_card_number}`}
                          className={`${sc.bg} ${sc.text} h-7 w-[120px] border-0 text-[11px] font-semibold rounded-full`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {["Pending", "In Progress", "Completed", "Delivered"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-3 py-3 text-[#64748B]">{formatDate(card.submitted_date)}</td>
                    <td className="px-3 py-3">
                      {invoice ? (
                        <div className="text-[11px]">
                          <span className="text-[#3B82F6] font-semibold">{invoice.invoice_number}</span>
                          <br/>
                          <span className={invoice.status === 'Paid' ? 'text-[#4CAF50]' : 'text-[#FF9800]'}>
                            {formatINR(invoice.amount)} · {invoice.status}
                          </span>
                        </div>
                      ) : card.status === 'Delivered' ? (
                        <Button data-testid={`create-invoice-${card.job_card_number}`} size="sm" variant="outline"
                          onClick={() => { setInvoiceJobCard(card); setInvoiceModalOpen(true); }}
                          className="h-6 text-[10px] gap-0.5 text-[#3B82F6] border-[#BFDBFE]">
                          <FileText className="w-3 h-3" /> Invoice
                        </Button>
                      ) : <span className="text-[11px] text-[#CBD5E1]">-</span>}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <div className="flex justify-end gap-0.5">
                        <button data-testid={`view-button-${card.job_card_number}`}
                          onClick={() => { setSelectedJobCard(card); setNotesModalOpen(true); }}
                          className="p-1.5 rounded-md hover:bg-[#F1F5F9] text-[#94A3B8] hover:text-[#475569] transition-colors">
                          <Eye className="w-4 h-4" />
                        </button>
                        <button data-testid={`edit-button-${card.job_card_number}`}
                          onClick={() => { setEditingCard(card); setModalOpen(true); }}
                          className="p-1.5 rounded-md hover:bg-[#F1F5F9] text-[#94A3B8] hover:text-[#475569] transition-colors">
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button className="p-1.5 rounded-md hover:bg-[#F1F5F9] text-[#94A3B8] hover:text-[#475569] transition-colors">
                              <MoreVertical className="w-4 h-4" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleDelete(card.id)} className="text-red-600 gap-2">
                              <Trash2 className="w-3.5 h-3.5" /> Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {filtered.length > 0 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-[#F1F5F9]" data-testid="pagination-bar">
            <div className="flex items-center gap-3">
              <span className="text-[12px] text-[#94A3B8]">Rows per page:</span>
              <Select value={String(rowsPerPage)} onValueChange={(v) => setRowsPerPage(Number(v))}>
                <SelectTrigger data-testid="rows-per-page" className="h-8 w-[70px] text-[12px] border-[#E2E8F0]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[10, 25, 50].map(n => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                </SelectContent>
              </Select>
              <span className="text-[12px] text-[#94A3B8]">
                {startRow}–{endRow} of {filtered.length}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <button data-testid="page-prev" onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                className="p-1.5 rounded-md hover:bg-[#F1F5F9] text-[#64748B] disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-[12px] font-medium text-[#1E293B] min-w-[60px] text-center">
                {page + 1} / {totalPages}
              </span>
              <button data-testid="page-next" onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="p-1.5 rounded-md hover:bg-[#F1F5F9] text-[#64748B] disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <JobCardModal open={modalOpen} onClose={(r) => { setModalOpen(false); setEditingCard(null); if (r) { fetchJobCards(); fetchInvoices(); }}} editingCard={editingCard} />
      <NotesHistoryModal open={notesModalOpen} onClose={() => setNotesModalOpen(false)} jobCard={selectedJobCard} />
      <InvoiceModal open={invoiceModalOpen} onClose={(r) => { setInvoiceModalOpen(false); setInvoiceJobCard(null); if (r) fetchInvoices(); }} jobCard={invoiceJobCard} />
    </div>
  );
};

export default JobCardsPage;
